/**
 * Phase 3.5A — typed read wrappers for the Methodology & Composition console.
 *
 * Every call below is a governed, read-only SECURITY DEFINER RPC that
 * enforces "active platform administrator" server-side. The console never
 * writes through direct table access and never treats a browser-supplied
 * status, hash or readiness result as authoritative.
 */
import { supabase } from "@/integrations/supabase/client";

// NOTE: `supabase.rpc` MUST stay bound to the client instance. Detaching the
// method (`const rpc = supabase.rpc`) loses `this` and every call fails inside
// the SDK with "Cannot read properties of undefined (reading 'rest')".
const rpc = ((fn: string, args?: Record<string, unknown>) =>
  (supabase.rpc as any)(fn, args)) as (
  fn: string,
  args?: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message: string } | null }>;


async function call<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

/* ------------------------------ types ------------------------------ */

export type FeatureFlags = {
  runtime_enabled: boolean;
  runtime_blocked_reason: string | null;
  composition_enabled: boolean;
  applicability_enabled: boolean;
  lineage_enabled: boolean;
  report_watermark_enabled: boolean;
};

export type ReadinessResult = {
  ready?: boolean;
  eligible?: boolean;
  reason_codes?: string[];
  [k: string]: unknown;
};

export type MethodologyOverview = {
  flags: FeatureFlags;
  runtime_activation: ReadinessResult;
  taxonomy: Array<{
    taxonomy_key: string;
    version_label: string;
    status: string;
    content_hash: string | null;
  }> | null;
  modules: {
    module_count: number;
    version_count: number;
    by_status: Record<string, number>;
    blocked_count: number;
  };
  question_versions: Record<string, number>;
  control_mappings: Record<string, number>;
  blueprints: Record<string, number>;
  executions: { total: number; succeeded: number; failed: number };
  publication_reviews: Record<string, number>;
  assessments: { total: number; composed: number; dna_records: number };
};

export type TaxonomyVersionRow = {
  id: string;
  taxonomy_key: string;
  version_label: string;
  status: string;
  content_hash: string | null;
  effective_from: string | null;
  effective_to: string | null;
  node_count: number;
  edge_count: number;
  alias_count: number;
  reviewed_at: string | null;
  approved_at: string | null;
  published_at: string | null;
  created_at: string | null;
  notes: string | null;
};

export type TaxonomyNodeRow = {
  node_id: string;
  node_key: string;
  kind: string;
  label: string;
  parent_label: string | null;
  description: string | null;
  attributes: Record<string, unknown> | null;
  deprecated: boolean;
  aliases: string[];
  outgoing_edges: Array<{
    edge_type: string;
    to_label: string;
    to_kind: string;
    attributes: Record<string, unknown> | null;
  }>;
};

export type ModuleVersionRow = {
  module_version_id: string;
  module_id: string;
  module_key: string;
  title: string;
  module_kind: string;
  version_label: string;
  status: string;
  selection_mode: string | null;
  question_count: number | null;
  estimated_minutes: number | null;
  content_hash: string | null;
  supersedes_version_id: string | null;
  superseded_by_version_id: string | null;
  publication_blocked: boolean;
  blocker_reason: string | null;
  replacement_version: string | null;
  summary: string | null;
  created_at: string | null;
  reviewed_at: string | null;
  approved_at: string | null;
  published_at: string | null;
};

export type CheckResult = { check_key: string; ok: boolean; detail: string | null };

export type ModuleVersionDetail = {
  module_version_id: string;
  header: Record<string, unknown>;
  manifest: Record<string, unknown>;
  validation: CheckResult[];
  publication_readiness: ReadinessResult;
  taxonomy_scope: Array<Record<string, unknown>>;
  questions: Array<Record<string, unknown>>;
  dependencies: Array<Record<string, unknown>>;
  score_definitions: Array<Record<string, unknown>>;
  evidence_expectations: Array<Record<string, unknown>>;
  benchmark_dimensions: Array<Record<string, unknown>>;
  lifecycle_log: Array<Record<string, unknown>>;
};

export type QuestionVersionRow = {
  id: string;
  question_key: string;
  version_label: string;
  title: string | null;
  status: string;
  content_hash: string | null;
  effective_from: string | null;
  effective_to: string | null;
  reviewed_at: string | null;
  approved_at: string | null;
  published_at: string | null;
  used_by_modules: string[];
};

export type CatalogRow = {
  id: string;
  artifact_type: string;
  artifact_key: string;
  version_label: string;
  status: string;
  content_hash: string | null;
  effective_from: string | null;
  effective_to: string | null;
  reviewed_at: string | null;
  approved_at: string | null;
  published_at: string | null;
  created_at: string | null;
  detail: Record<string, unknown>;
};

export type PublicationReviewRow = {
  review_id: string;
  artifact_type: string;
  artifact_version_id: string;
  artifact_label: string | null;
  artifact_status: string | null;
  review_status: string;
  checklist_key: string | null;
  checklist_version: string | null;
  items_total: number;
  items_passed: number;
  items_failed: number;
  items_pending: number;
  readiness_hash: string | null;
  legacy_state: string | null;
  reviewed_at: string | null;
  approved_at: string | null;
  created_at: string | null;
};

export type PublicationReviewDetail = {
  review: Record<string, unknown>;
  readiness: ReadinessResult;
  checklist: Record<string, unknown> | null;
  items: Array<{
    item_id: string;
    item_key: string;
    item_label: string;
    category: string | null;
    description: string | null;
    guidance: string | null;
    validation_mode: string;
    automated_validator: string | null;
    required: boolean;
    severity: string | null;
    ordinal: number | null;
    status: string;
    automated_result: string | null;
    reviewer_result: string | null;
    reviewer_notes: string | null;
    evidence_reference: string | null;
    completed_by: string | null;
    completed_at: string | null;
  }>;
};

export type BlueprintRow = {
  id: string;
  assessment_id: string | null;
  blueprint_version: number;
  status: string;
  is_current: boolean;
  questionnaire_id: string | null;
  module_count: number | null;
  question_count: number | null;
  estimated_minutes: number | null;
  publication_ready: boolean | null;
  blueprint_hash: string | null;
  generated_at: string | null;
};

export type BlueprintDetail = {
  blueprint: Record<string, unknown>;
  validation: CheckResult[];
  launch_readiness: ReadinessResult;
  modules: Array<Record<string, unknown>>;
  question_count: number;
  questions: Array<Record<string, unknown>>;
  executions: Array<Record<string, unknown>>;
};

export type ExecutionRow = {
  execution_id: string;
  assessment_id: string | null;
  blueprint_id: string | null;
  status: string;
  failure_stage: string | null;
  failure_code: string | null;
  module_count: number | null;
  question_count: number | null;
  execution_hash: string | null;
  executed_at: string | null;
};

export type ManifestRow = {
  id: string;
  assessment_id: string;
  dna_version: number;
  is_current: boolean;
  status: string;
  record_kind: string;
  portfolio_id: string | null;
  portfolio_name: string | null;
  company_id: string | null;
  questionnaire_id: string | null;
  question_count: number | null;
  content_hash: string | null;
  generated_at: string | null;
  frozen_at: string | null;
};

export type ProvenanceRow = {
  id: string;
  event_kind: string;
  event_status: string | null;
  occurred_at: string;
  actor_role: string | null;
  methodology_manifest_hash: string | null;
  question_set_hash: string | null;
  payload: Record<string, unknown> | null;
};

export type AssessmentSafety = {
  assessment_id: string;
  report_classification: ReadinessResult & { classification?: string };
  watermark_required: boolean;
  benchmark_eligibility: ReadinessResult;
  ai_eligibility: ReadinessResult;
  launch_readiness: ReadinessResult;
  lineage: Record<string, unknown>;
  governance_profile: Record<string, unknown> | null;
  composed: boolean;
  provenance_event_count: number;
};

export type GovernanceProfileRow = {
  id: string;
  profile_key: string;
  version_label: string;
  tenant_only: boolean;
  portfolio_aggregation_allowed: boolean;
  anonymous_benchmarking_allowed: boolean;
  ai_assisted_analysis_allowed: boolean;
  ai_training_allowed: boolean;
  commercial_intelligence_allowed: boolean;
  retention_months: number | null;
  automatic_deletion_enabled: boolean | null;
  min_anonymization_k: number | null;
  min_cohort_size: number | null;
  notes: string | null;
};

/* ------------------------------ reads ------------------------------ */

export const getFeatureFlags = () => call<FeatureFlags>("admin_feature_flags");
export const getMethodologyOverview = () =>
  call<MethodologyOverview>("admin_methodology_overview");
export const listTaxonomyVersions = () =>
  call<TaxonomyVersionRow[]>("admin_list_taxonomy_versions");
export const listTaxonomyNodes = (
  taxonomyVersionId: string,
  kind?: string | null,
  search?: string | null,
) =>
  call<TaxonomyNodeRow[]>("admin_list_taxonomy_nodes", {
    p_taxonomy_version_id: taxonomyVersionId,
    p_kind: kind ?? null,
    p_search: search ?? null,
  });
export const listModuleVersions = () =>
  call<ModuleVersionRow[]>("admin_list_module_versions");
export const getModuleVersionDetail = (id: string) =>
  call<ModuleVersionDetail>("admin_get_module_version_detail", {
    p_module_version_id: id,
  });
export const listQuestionVersions = (search?: string | null, status?: string | null) =>
  call<QuestionVersionRow[]>("admin_list_question_versions", {
    p_search: search ?? null,
    p_status: status ?? null,
    p_limit: 500,
  });
export const listMethodologyCatalog = (artifactType: string) =>
  call<CatalogRow[]>("admin_list_methodology_catalog", { p_artifact_type: artifactType });
export const listPublicationReviews = () =>
  call<PublicationReviewRow[]>("admin_list_publication_reviews");
export const getPublicationReview = (reviewId: string) =>
  call<PublicationReviewDetail>("admin_get_publication_review", { p_review_id: reviewId });
export const listBlueprints = (limit = 100, offset = 0) =>
  call<BlueprintRow[]>("admin_list_blueprints", { p_limit: limit, p_offset: offset });
export const getBlueprintDetail = (id: string) =>
  call<BlueprintDetail>("admin_get_blueprint_detail", { p_blueprint_id: id });
export const listExecutions = (limit = 100) =>
  call<ExecutionRow[]>("admin_list_composition_executions", { p_limit: limit });
export const listManifests = (limit = 100, offset = 0) =>
  call<ManifestRow[]>("admin_list_assessment_dna", { p_limit: limit, p_offset: offset });
export const listProvenanceEvents = (assessmentId: string) =>
  call<ProvenanceRow[]>("admin_list_provenance_events", { p_assessment_id: assessmentId });
export const getAssessmentSafety = (assessmentId: string) =>
  call<AssessmentSafety>("admin_get_assessment_safety", { p_assessment_id: assessmentId });
export const listGovernanceProfiles = () =>
  call<GovernanceProfileRow[]>("admin_list_governance_profiles");
export const getRuntimeActivationReadiness = () =>
  call<ReadinessResult>("composition_runtime_activation_readiness");

/* ------------------------- governed writes ------------------------- */
/**
 * The only mutating calls the console exposes. Each is a governed lifecycle
 * or review RPC that performs its own authorization and readiness checks
 * server-side; the UI cannot bypass them.
 */
export const recordPublicationReviewItem = (
  reviewId: string,
  itemKey: string,
  result: "passed" | "failed",
  notes?: string | null,
  evidenceReference?: string | null,
) =>
  call<Record<string, unknown>>("admin_record_publication_review_item", {
    p_review_id: reviewId,
    p_item_key: itemKey,
    p_result: result,
    p_notes: notes ?? null,
    p_evidence_reference: evidenceReference ?? null,
  });

export const runPublicationAutomatedChecks = (reviewId: string) =>
  call<Record<string, unknown>>("admin_run_publication_automated_checks", {
    p_review_id: reviewId,
  });

export const completePublicationReview = (reviewId: string, notes?: string | null) =>
  call<Record<string, unknown>>("admin_complete_publication_review", {
    p_review_id: reviewId,
    p_notes: notes ?? null,
  });

export const transitionModuleVersion = (
  moduleVersionId: string,
  toStatus: string,
  reason: string,
) =>
  call<string>("admin_transition_module_version", {
    p_module_version_id: moduleVersionId,
    p_to_status: toStatus,
    p_reason: reason,
  });
